import { ISavedChat } from "../../modules/chat/types";
import { CoCQueryRequest, CoCApiResponse } from "../../modules/chainOfCustody/types";
import { getChatServerUrl, getUsageReportingUrl, getWorkflowServerUrl } from "../common/utils";
import { IFetchUserPayload } from "../slices/userSlice";
import apiRequest from "./apiRequest";

const workflowServerUrl = getWorkflowServerUrl();
const chatServerUrl = getChatServerUrl();
const usageReportUrl = getUsageReportingUrl();
export const apiEndpoints = {
    agent: `${workflowServerUrl}/agent`,
    agents: `${workflowServerUrl}/agents`,
    agentUsage: `${workflowServerUrl}/agent/usage`,
    task: `${workflowServerUrl}/task`,
    tasks: `${workflowServerUrl}/tasks`,
    taskUsage: `${workflowServerUrl}/task/usage`,
    workFlow: `${workflowServerUrl}/workflow`,
    workFlows: `${workflowServerUrl}/workflows`,
    tools: `${workflowServerUrl}/tools`,
    workflowSession: `${workflowServerUrl}/session`,
    workflowSessions: `${workflowServerUrl}/sessions`,
    deleteWorkflowSession: `${workflowServerUrl}/delete/session`,
    executeSession: `${workflowServerUrl}/workflow/execute`,
    sessionResult: `${workflowServerUrl}/workflow/execution/result`,
    fetchResource: `${workflowServerUrl}/get-resource`,
    deleteResource: `${workflowServerUrl}/delete-resource`,
    deleteCheck: `${workflowServerUrl}/pre-delete-check`,
    artifactDelete: `${workflowServerUrl}/delete`,
    workflowsPresignedUrl: `${workflowServerUrl}/generate-presigned-url`,
    authUrl: `${chatServerUrl}/get-auth-url`,
    presignedUrl: `${chatServerUrl}/presigned-url`,
    sessionUrl: `${chatServerUrl}/sessions`,
    userDetails: `${chatServerUrl}/user`,
    modelConfigs: `${chatServerUrl}/model-configs`,
    translation: `${chatServerUrl}/translation`,
    presentation: `${chatServerUrl}/presentation/generate`,
    powerpointSlideCount: `${chatServerUrl}/presentation/slide-content`,
    powerpointEmbedContent: `${chatServerUrl}/presentation/embed-content`,
    userGuide: `${chatServerUrl}/user-guide`,
    releaseNotes: `${chatServerUrl}/release-notes`,
    knowledgeBase: `${chatServerUrl}/knowledge-base`,
    kbfiles: `${chatServerUrl}/get_kb_files_list`,
    validateIndex: `${chatServerUrl}/validate-index`,
    modifyKB: `${chatServerUrl}/knowledge-base/modify`,
    exportChat: `${chatServerUrl}/chat_export`,
    conversation: `${chatServerUrl}/conversation`,
    savedChat: `${chatServerUrl}/saved-chat`,
    chunkSettings: `${chatServerUrl}/chunk-settings`,
    kbStatus: `${chatServerUrl}/kb-status`,
    cocRecords: `${chatServerUrl}/coc/records`,
    cocFile: `${chatServerUrl}/coc/file`,
    feedback: `${chatServerUrl}/feedback`,
};

export const getUser = async (payload: IFetchUserPayload): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.userDetails, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch user details",
        );
    }
};

export const getModelConfigs = async (): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.modelConfigs, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch model configs"
        );
    }
};

export const getTranslationLanguages = async (): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.translation, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch translation languages"
        );
    }
};

export const translateDoc = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.translation, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to translate document"
        );
    }
}

export const generatePowerpoint = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.presentation, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to generate presentation"
        );
    }
}

export const generatePowerpointContent = async (pathParams: string, payload: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.presentation}/${pathParams}`, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to generate presentation"
        );
    }
}

export const getUserGuide = async (queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.userGuide}?${queryParams}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch user guide"
        );
    }
};

export const getReleaseNotes = async (): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.releaseNotes, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch release notes"
        );
    }
};

export const getKnowledgeBases = async (queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.knowledgeBase}?${queryParams}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch knowledge bases"
        );
    }
};

export const getFilesForKnowledgeBase = async (queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.kbfiles}?${queryParams}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch files under a knowledge base"
        );
    }
};

export const getStatusForKnowledgeBase = async (queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.kbStatus}?${queryParams}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch status for knowledge base"
        );
    }
};

export const getPresignedUrl = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.presignedUrl, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch presigned URL"
        );
    }
};

export const getWorkflowsPresignedUrl = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.workflowsPresignedUrl, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch presigned URL"
        );
    }
};

export const deleteKB = async (queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.knowledgeBase}?${queryParams}`, "DELETE");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to delete knowledge base"
        );
    }
};

export const createSession = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.sessionUrl, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to create session"
        );
    }
}

export const validateIndex = async (queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.validateIndex}?${queryParams}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to validate index"
        );
    }
}

export const modifyKB = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.modifyKB, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to modify KB"
        );
    }
}

export const createKB = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.knowledgeBase, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to create knowledge base"
        );
    }
}

export const getChunkSettings = async (): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.chunkSettings, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch chunk settings"
        );
    }
};

export const exportChatByFormat = async (payload: any): Promise<any> => {
    try {
        return await fetch(apiEndpoints.exportChat, payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to export chat"
        );
    }
}

export const generateAIResponse = async (payload: any): Promise<any> => {
    try {
        return await fetch(apiEndpoints.conversation, payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to generate AI Response"
        );
    }
}

export const pollSession = async (pathParam: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.sessionUrl}/${pathParam}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to poll status"
        );
    }
}

export const uploadToS3 = async (file: any, signedUrl: any) => {
    try {
        const response = await fetch(signedUrl.presigned_url, {
            method: "PUT",
            body: file,
        });
        if (!response.ok) {
            throw new Error("Failed to upload file.");
        }
        return response;
    } catch (error) {
        throw error;
    }
};

export const getPowerpointSlideContent = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.powerpointSlideCount, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch content for PowerPoint slides"
        );
    }
}

export const embedPowerpointSlideContent = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.powerpointEmbedContent, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to embed PowerPoint content"
        );
    }
}

export const getAgents = async (queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.agents}?${queryParams}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch agents"
        );
    }
}

export const createAgent = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.agent, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to create agent"
        );
    }
}

export const fetchAgentById = async (pathParam: any, queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.agent}/${pathParam}?${queryParams}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : `Failed to fetch agent: ${pathParam}`
        );
    }
}

export const updateAgentById = async (pathParam: any, payload: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.agent}/${pathParam}`, "PUT", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to update agent"
        );
    }
}

export const updateAgentUsage = async (pathParam: any, payload: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.agentUsage}/${pathParam}`, "PUT", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to update agent"
        );
    }
}

export const getTasks = async (queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.tasks}?${queryParams}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch tasks"
        );
    }
}

export const createTask = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.task, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to create task"
        );
    }
}

export const fetchTaskById = async (pathParam: any, queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.task}/${pathParam}?${queryParams}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : `Failed to fetch task: ${pathParam}`
        );
    }
}

export const updateTaskById = async (pathParam: any, payload: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.task}/${pathParam}`, "PUT", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to update workflow"
        );
    }
}

export const updateTaskUsage = async (pathParam: any, payload: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.taskUsage}/${pathParam}`, "PUT", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to update task"
        );
    }
}

export const getWorkflows = async (queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.workFlows}?${queryParams}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch workflows"
        );
    }
}

export const createWorkflow = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.workFlow, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to create workflow"
        );
    }
}

export const fetchWorkflowById = async (pathParam: any, queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.workFlow}/${pathParam}?${queryParams}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : `Failed to fetch workflow: ${pathParam}`
        );
    }
}

export const updateWorkflowById = async (pathParam: any, payload: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.workFlow}/${pathParam}`, "PUT", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to update workflow"
        );
    }
}

export const getTools = async (): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.tools, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch tools"
        );
    }
}

export const getSessions = async (queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.workflowSessions}?${queryParams}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch sessions"
        );
    }
}

export const fetchSessionById = async (pathParam: any, queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.workflowSession}/${pathParam}?${queryParams}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : `Failed to fetch session: ${pathParam}`
        );
    }
}

export const getSessionResult = async (pathParam: any, queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.sessionResult}/${pathParam}?${queryParams}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch session result"
        );
    }
}

export const createWorkflowSession = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.workflowSession, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to create session"
        );
    }
}

export const deleteSessionById = async (pathParam: any, queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.deleteWorkflowSession}/${pathParam}?${queryParams}`, "DELETE");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : `Failed to delete session: ${pathParam}`
        );
    }
}

export const executeSession = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.executeSession, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to execute session"
        );
    }
}

export const isArtifactDeletable = async (pathParam: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.deleteCheck}/${pathParam}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to know if this artifact is deletable"
        );
    }
}

export const deleteArtifact = async (pathParam: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.artifactDelete}/${pathParam}`, "DELETE");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to delete artifact"
        );
    }
}

export const getResource = async (queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.fetchResource}?${queryParams}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch resource"
        );
    }
}

export const deleteResource = async (queryParams: any): Promise<any> => {
    try {
        return await apiRequest(`${apiEndpoints.deleteResource}?${queryParams}`, "DELETE");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to delete resource"
        );
    }
}

export const getUsageReport = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(usageReportUrl, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch usage report"
        );
    }
}

export const getSavedChats = async (user: any, queryParams?: string): Promise<any> => {
    try {
        const url = queryParams ? `${apiEndpoints.savedChat}/${user}?${queryParams}` : `${apiEndpoints.savedChat}/${user}`;
        return await apiRequest(url, 'GET');
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : 'Failed to get saved chats'
        );
    }
}

export const getOneSavedChat = async (
    user: any,
    timestamp: string
): Promise<any> => {
    try {
        return await apiRequest(
            `${apiEndpoints.savedChat}/${user}/${timestamp}`,
            'GET'
        );
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : 'Failed to get saved chats'
        );
    }
};

export const saveSavedChat = async (
    payload: ISavedChat,
    isUpdate: boolean
): Promise<any> => {
    try {
        return await apiRequest(
            apiEndpoints.savedChat,
            isUpdate ? 'PUT' : 'POST',
            payload
        );
    } catch (error) {
        throw new Error(
            error instanceof Error
                ? error.message
                : `Failed to ${isUpdate ? 'update' : 'save'} saved chat`
        );
    }
};
export const deleteSavedChat = async (
    user: any,
    timestamp: string
): Promise<any> => {
    try {
        return await apiRequest(
            `${apiEndpoints.savedChat}/${user}/${timestamp}`,
            'DELETE'
        );
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : 'Failed to delete saved chat'
        );
    }
};

// Chain of Custody API functions
export const getCoCRecords = async (query: CoCQueryRequest): Promise<CoCApiResponse> => {
    try {
        return await apiRequest(apiEndpoints.cocRecords, "POST", query);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch CoC records"
        );
    }
};

export const getCoCRecordsByFile = async (s3Path: string): Promise<CoCApiResponse> => {
    try {
        const encodedPath = encodeURIComponent(s3Path);
        return await apiRequest(`${apiEndpoints.cocFile}/${encodedPath}`, "GET");
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to fetch CoC records by file"
        );
    }
};

export const createFeedback = async (payload: any): Promise<any> => {
    try {
        return await apiRequest(apiEndpoints.feedback, "POST", payload);
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : "Failed to save feedback"
        );
    }
};
